import React from 'react';
import { Link } from '@tanstack/react-router';
import { MapPin, Calendar, Heart, MessageCircle } from 'lucide-react';
import { cn, Image as ImageComponent } from '@city-sage/aurum-avis-labs-components';
import { Itinerary } from '@city-sage/core-client';
import { MarkdownRenderer } from '../MarkdownRenderer';

interface ItinerarySearchCardProps {
  itinerary: Itinerary;
  searchQuery: string;
  className?: string;
  onClose?: () => void;
}

export function ItinerarySearchCard({ 
  itinerary, 
  searchQuery, 
  className,
  onClose
}: ItinerarySearchCardProps) {
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-black font-medium">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getCoverImage = () => {
    if (itinerary.imageUrl) {
      return itinerary.imageUrl;
    }
    return undefined;
  };

  const getDescription = () => {
    return itinerary.shortDescription || itinerary.description || '';
  };

  return (
    <Link to="/itineraries/$id" params={{ id: itinerary.id }} onClick={onClose}>
      <div className={cn(
        "bg-white px-6 py-4 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0",
        className
      )}>
        <div className="flex gap-4">
          {/* Image */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
              {getCoverImage() ? (
                <ImageComponent
                  src={getCoverImage()}
                  image={itinerary.image}
                  alt={itinerary.title}
                  className="w-full h-full"
                  imageClassName="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-blue-500" />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-1.5">
            {/* Title */}
            <h3 className="font-semibold text-base text-gray-900 truncate">
              {highlightText(itinerary.title || 'Untitled Itinerary', searchQuery)}
            </h3>

            {/* Description */}
            {getDescription() && (
              <div className="text-sm text-gray-600 line-clamp-1">
                <MarkdownRenderer content={getDescription()} className="text-sm text-gray-600" />
              </div>
            )}

            {/* Days & Stops Count */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              {itinerary.daysCount > 0 && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{itinerary.daysCount} day{itinerary.daysCount !== 1 ? 's' : ''}</span>
                </div>
              )}
              
              {itinerary.stopsCount > 0 && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span>{itinerary.stopsCount} stop{itinerary.stopsCount !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            {/* Likes & Comments */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                <span>0</span>
              </div>
              
              <div className="flex items-center gap-1">
                <MessageCircle className="h-3 w-3" />
                <span>0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
} 